import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Table,
  Loader,
  Center,
  Checkbox,
  SimpleGrid,
  ScrollArea,
  Box,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useRolesAdmin } from "../../../hooks/admin/useRoles";
import {
  useRoleTasksByRole,
  useSetRoleTasks,
} from "../../../hooks/admin/useRoleTasks";
import { useTasksAdmin } from "../../../hooks/admin/useTasks";
import { Role } from "../../../services/admin/roles.service";

function EditRoleTasksModal({
  role,
  opened,
  onClose,
}: {
  role: Role;
  opened: boolean;
  onClose: () => void;
}) {
  const { data: tasks = [], isLoading: tasksLoading } = useTasksAdmin();
  const { data: roleTasksData, isLoading: roleTasksLoading } =
    useRoleTasksByRole(role.id);
  const setRoleTasks = useSetRoleTasks();

  const activeTasks = tasks.filter((t) => t.isActive);
  const currentTaskIds = new Set((roleTasksData?.tasks || []).map((t) => t.id));
  const [draft, setDraft] = useState<Set<string>>(currentTaskIds);

  // Sync draft when data loads
  const initialDraft = roleTasksLoading
    ? new Set<string>()
    : new Set((roleTasksData?.tasks || []).map((t) => t.id));
  const [initialized, setInitialized] = useState(false);
  if (!roleTasksLoading && !initialized) {
    setDraft(initialDraft);
    setInitialized(true);
  }

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    if (draft.size === 0) {
      notifications.show({
        title: "Validation error",
        message: "At least one task must be assigned to a role.",
        color: "orange",
      });
      return;
    }
    setRoleTasks.mutate(
      { roleId: role.id, taskIds: Array.from(draft) },
      { onSuccess: onClose }
    );
  };

  const isLoading = tasksLoading || roleTasksLoading;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Tasks — ${role.name}`}
      size="xl"
      closeOnClickOutside={false}
      styles={{ body: { padding: 0 } }}
    >
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <>
          <ScrollArea.Autosize
            mah="calc(100vh - 300px)"
            offsetScrollbars
            px="md"
            pt="md"
          >
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Select the tasks this role is allowed to perform.
              </Text>
              <SimpleGrid cols={3}>
                {activeTasks.map((task) => (
                  <Checkbox
                    key={task.id}
                    label={task.name}
                    checked={draft.has(task.id)}
                    onChange={() => toggle(task.id)}
                    color="gcpBlue"
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </ScrollArea.Autosize>

          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                color="gcpBlue"
                onClick={handleSave}
                loading={setRoleTasks.isPending}
                disabled={draft.size === 0}
              >
                Save
              </Button>
            </Group>
          </Box>
        </>
      )}
    </Modal>
  );
}

export default function RoleTasksAdminPage() {
  const { data: roles = [], isLoading } = useRolesAdmin();
  const activeRoles = roles.filter((r) => r.isActive);

  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    openEdit();
  };

  const handleClose = () => {
    closeEdit();
    setEditingRole(null);
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        <div>
          <Title order={2}>Role Tasks</Title>
          <Text c="dimmed" size="sm">
            Configure which tasks each role can access. Click a role to edit its
            task assignments.
          </Text>
        </div>

        <Alert
          icon={<IconAlertCircle size={16} />}
          color="blue"
          variant="light"
        >
          Changes take effect on each user's next login or task selection.
        </Alert>

        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Role</Table.Th>
              <Table.Th>Assigned Tasks</Table.Th>
              <Table.Th w={100}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {activeRoles.map((role) => (
              <RoleTaskRow
                key={role.id}
                role={role}
                onEdit={() => handleEdit(role)}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Stack>

      {editingRole && (
        <EditRoleTasksModal
          role={editingRole}
          opened={editOpened}
          onClose={handleClose}
        />
      )}
    </Container>
  );
}

function RoleTaskRow({ role, onEdit }: { role: Role; onEdit: () => void }) {
  const { data } = useRoleTasksByRole(role.id);
  const tasks = data?.tasks || [];

  return (
    <Table.Tr>
      <Table.Td fw={500}>{role.name}</Table.Td>
      <Table.Td>
        <Group gap={4} wrap="wrap">
          {tasks.length === 0 ? (
            <Text size="sm" c="dimmed">
              No tasks assigned
            </Text>
          ) : (
            tasks.map((t) => (
              <Badge key={t.id} variant="light" color="gcpBlue" size="sm">
                {t.name}
              </Badge>
            ))
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Button size="xs" variant="light" onClick={onEdit}>
          Edit
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}
